import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type {
  LiveSession,
  LiveSessionProvider,
  LiveSessionStatus,
} from "@/lib/types";

export function useSessions() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const sessionsQuery = useQuery({
    queryKey: ["live-sessions", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("live_sessions")
        .select("*")
        .eq("user_id", user.id)
        .order("started_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as LiveSession[];
    },
    enabled: !!user,
  });

  const createSession = useMutation({
    mutationFn: async (input: {
      provider: LiveSessionProvider;
      project_id?: string | null;
      repo?: string | null;
      title?: string | null;
    }) => {
      if (!user) throw new Error("Not authenticated");

      // Check session limits: if no ai_provider integration with matching key, max 1 active
      const activeSessions = (sessionsQuery.data ?? []).filter(
        (s) => s.status === "active" || s.status === "starting"
      );

      const { data: integrations } = await supabase
        .from("integrations")
        .select("*")
        .eq("user_id", user.id)
        .eq("type", "ai_provider")
        .eq("enabled", true);

      // Claude Code uses CLI auth (no API key needed) — always unlimited
      // Codex/Gemini need API keys for unlimited sessions
      const isCliAuth = input.provider === "claude-code";

      const hasProviderKey = isCliAuth || integrations?.some((i) => {
        if (!i.config || typeof i.config !== "object") return false;
        const cfg = i.config as Record<string, string>;
        if (input.provider === "codex") return !!cfg.openai_key;
        if (input.provider === "gemini") return !!cfg.google_key;
        return false;
      });

      if (!hasProviderKey && activeSessions.length >= 1) {
        throw new Error(
          "Free tier limited to 1 active session. Connect your provider in Settings to unlock more."
        );
      }

      const { data, error } = await supabase
        .from("live_sessions")
        .insert({
          user_id: user.id,
          provider: input.provider,
          project_id: input.project_id ?? null,
          repo: input.repo ?? null,
          title: input.title ?? null,
          status: "starting" as LiveSessionStatus,
          metadata: {},
        })
        .select()
        .single();

      if (error) throw error;
      return data as LiveSession;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["live-sessions"] });
    },
  });

  const endSession = useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase
        .from("live_sessions")
        .update({
          status: "ended" as LiveSessionStatus,
          ended_at: new Date().toISOString(),
        })
        .eq("id", sessionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["live-sessions"] });
    },
  });

  const reactivateSession = useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase
        .from("live_sessions")
        .update({
          status: "starting" as LiveSessionStatus,
          ended_at: null,
          machine_id: null,
        })
        .eq("id", sessionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["live-sessions"] });
    },
  });

  const deleteSession = useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase
        .from("live_sessions")
        .delete()
        .eq("id", sessionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["live-sessions"] });
    },
  });

  return {
    sessions: sessionsQuery.data ?? [],
    isLoading: sessionsQuery.isLoading,
    createSession,
    endSession,
    reactivateSession,
    deleteSession,
  };
}
